<template>
  <section>
    <template v-if="order">
      <!-- Notification for revise cleaning datetime -->
      <div v-if="displayNotification(order, 'revisingDateTime')">
        <div class="section-notification text-center">
          {{ $t('message.revise_datetime.cleaner_sent_revising_datetime_request') }}
          <svg class="notification-alert">
            <circle cx="3" cy="3" r="3"/>
          </svg>
        </div>
      </div>
    </template>

    <!-- Section content -->
    <section>
      <template v-if="user && order && Number(user.id) === Number(order.user_id)">
        <div class="row py-4">
          <div class="col">
            <router-link :to="isLineLoggedIn ? '/line-liff/bookings' : '/my-reservations'" class="text-primary-link">{{ $t('message.menu.my_reservations') }}</router-link>
            <i class="fal fa-angle-right"></i>
            {{ $t('message.my_reservations.reservation_detail') }}
          </div>
        </div>

        <div class="row mb-4 d-flex h-100">
          <div class="col-7 align-self-center">
            <label class="m-0">
              #{{ order.order_code }}
              -
              <span v-html="$options.filters.serviceParentName(order.service_id, globalState.services)"></span>
            </label>
          </div>
          <div class="col-5 text-right">
            <template v-if="order.status < 2">
              <template
                v-if="
                  order.payment_data.method === 'credit_card' &&
                    order.subscription === null &&
                    order.package_id === null
                "
              >
                <button
                  class="btn btn-outline-dark"
                  @click="openCancelOrderModal"
                >
                  {{ $t('message.button.cancel_order') }}
                </button>
              </template>
            </template>
          </div>
        </div>
        <!-- /.row -->

        <div class="row">
          <div class="col-lg-8">
            <!-- Received Order Section -->
            <received-order-section :order="order"/>

            <!-- Review Section -->
            <review-section :order="order"/>

            <!-- Confirm Bank Transfer Section -->
            <confirm-order-payment-section :order="order"/>

            <!-- Confirm Request Additional Hours Section -->
            <confirm-request-additional-hours-section :order="order"/>

            <!-- Find New Provider Section -->
            <!-- <find-new-provider :order="order" v-if="displayNotification(order, 'decliningBooking')"/> -->

            <!-- Repeat booking section -->
            <div class="card" v-if="!order.package_id && !order.subscription_id && !order.claimed_from">
              <div class="card-body">
                <div class="d-flex justify-content-start align-items-center">
                  <div class="flex-fill">
                    <i class="fas fa-repeat text-info"></i> {{ $t('message.my_reservations.repeat_booking_label') }}
                  </div>
                  <div>
                    <button class="btn btn-info" @click="repeatBooking(order)">
                      {{ $t('message.button.repeat_booking') }}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <section v-if="order.status === 2 && order.order_photos && order.sent_photo_status === 1">
              <div class="card panel-white">
                <div class="card-body">
                  <div class="mb-2">
                    <label>
                      <i class="fal fa-images"></i>
                      {{ $t('message.my_reservations.completed_cleaning_photos') }}
                    </label>
                  </div>
                  <photos-album
                    :photos="order.order_photos"
                    :photoType="'order_photos'"
                    :includeGallery="true"
                  />
                </div>
              </div>
            </section>

            <my-cleaner :order="order" 
                        :imgSize="56"
                        class="text-center d-block d-lg-none"/>

            <!-- Pre-booking Professional Selection Section -->
            <!-- <pre-booking-professional-selection-section :order="order"/> -->
            
            <div class="card panel-white">
              <div class="card-body">
                <div class="row no-gutters">
                  <div class="col-3">
                    <label>{{ $t('message.my_reservations.status') }}</label>
                  </div>
                  <div class="col-9">
                    <span v-html="$options.filters.orderStatus(order)"></span>
                  </div>
                </div>
              </div>
              <!-- Subscription -->
              <div class="card-body" v-if="order.subscription_id">
                <div class="row no-gutters">
                  <div class="col-md-3 mb-2">
                    <span class="text-orange">
                      <i class="fal fa-calendar-alt"></i>
                      {{ $t('message.subscription.title') }}
                    </span>
                  </div>
                  <div class="col-md-9">
                    {{ $t('message.subscription.title') }} #{{ order.subscription_id }}
                    - {{ order.subscription.subscription_type | multiLangName }}
                    <div class="mt-2">
                      {{ $t('message.subscription.next_subscription') }}:
                      <span class="text-info" v-if="order.subscription.subscription_group.cancel === 0">
                        {{ order.subscription.recurring_date | prettyOnlyDate }}
                      </span>
                      <span class="text-danger" v-else>
                        {{ $t('message.button.cancel') }}
                      </span>
                    </div>
                    <div class="mt-2">
                      <button class="btn btn-outline-secondary btn-block pointer-item" @click="openSubscriptionOrdersModal">
                        {{ $t('message.subscription.view_all_bookings') }}
                      </button>
                    </div>
                    <div
                      class="mt-2"
                      v-if="order.paid_status === 1 && order.subscription.subscription_group.cancel === 0">
                      <button class="btn btn-block btn-outline-info" @click="unsubscribe">
                        {{ $t('message.subscription.unsubscribe') }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Package -->
              <div class="card-body" v-if="order.package_id">
                <div class="row no-gutters">
                  <div class="col-md-3 mb-2">
                    <span class="text-orange">
                      <i class="fal fa-layer-group"></i>
                      {{ $t('message.my_reservations.package') }}
                    </span>
                  </div>
                  <div class="col-md-9">
                    {{ $t('message.my_reservations.package') }} #{{ order.package_id }}
                    - {{ order.package.package_type | multiLangName }}
                    <span v-if="order.package.package_type.category === 'unlimited'">
                      {{ $t('message.booking.new_booking.package_count', { count: order.package.orders.length }) }}
                    </span>
                    <div class="mt-3" v-if="order.package.package_type.is_hidden_info === 0">
                      <button
                        class="btn btn-outline-secondary btn-block pointer-item"
                        @click="openSubscriptionOrdersModal">
                        {{ $t('message.subscription.view_all_bookings') }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- For Host -->
              <div class="card-body" v-if="order.host">
                <div class="row no-gutters">
                  <div class="col">
                    <span class="text-orange">
                      <i class="fal fa-bed"></i>
                      {{ $t('message.my_reservations.for_host') }}
                    </span>
                    <div>
                      {{ $t('message.my_reservations.contract_number') }}
                      {{ order.host.contract_number }}
                    </div>
                  </div>
                </div>
              </div>

              <div class="card-body">
                <div class="row no-gutters">
                  <div class="col-md-3">
                    <label>{{ $t('message.my_reservations.cleaning_date') }}</label>
                  </div>
                  <div class="col-md-9">
                    <div class="d-flex">
                      <div class="flex-fill">
                        <template v-if="order.revise_datetime_data && order.status === 1">
                          <div class="strike text-secondary">
                            {{ order.cleaning_date | prettyDate }}
                          </div>
                          <div class="text-info">
                            {{ order.revise_datetime_data.cleaning_date | prettyDate }}
                          </div>
                          <template v-if="order.revise_datetime_data.sender_type === 'user'">
                            <div class="py-3">
                              <span class="bullet-notification">
                                <i class="fas fa-circle"></i>
                              </span>
                              {{ $t('message.my_reservations.you_want_to_change_date_time') }}
                            </div>
                            <button class="btn btn-outline-info btn-block"
                                    :disabled="state.isCancelingReviseDateTime"
                                    @click="cancelReviseDateTime">
                              {{ $t(isCancelingReviseDateTime) }}
                            </button>
                          </template>
                          <template v-else>
                            <div class="py-3">
                              <span class="bullet-notification">
                                <i class="fas fa-circle"></i>
                              </span>
                              {{ $t('message.my_reservations.cleaner_want_to_change_date_time') }}
                            </div>
                            <div class="d-flex" v-if="!state.isConfirmingReviseDateTime">
                              <div class="flex-fill pr-1">
                                <button class="btn btn-info btn-block" @click="acceptReviseDateTime">
                                  {{ $t('message.revise_datetime.accept') }}
                                </button>
                              </div>
                              <div class="flex-fill pl-1">
                                <button class="btn btn-outline-secondary btn-block" @click="declineReviseDateTime">
                                  {{ $t('message.revise_datetime.decline') }}
                                </button>
                              </div>
                            </div>
                            <template v-else>
                              <img src="/static/img/ring.gif" width="40"/>
                              {{ $t('message.processing') }}
                            </template>
                          </template>
                        </template>
                        <span v-else>
                          <template v-if="order.revise_datetime_data">
                            {{ order.revise_datetime_data.cleaning_date | prettyDate }}
                          </template>
                          <template v-else>
                            <span v-if="order.cleaning_date">{{ order.cleaning_date | prettyDate }}</span>
                            <span class="text-danger" v-else>{{ $t('message.my_reservations.booking_date_not_specified') }}</span>
                          </template>
                        </span>
                      </div>
                    </div>
                    <div class="pt-3" v-if="isAbleReviseDateTime(order)">
                      <button
                        class="btn btn-info btn-block"
                        @click="openReviseDateTimeModal">
                        {{ $t('message.my_reservations.change_date_time') }}
                      </button>
                    </div>
                    <!-- Select Service Date-Time Section for Flexible Package -->
                    <select-service-date-time-section class="mt-2" :order="order"/>
                  </div>
                </div>
              </div>
              <div class="card-body">
                <div class="row no-gutters">
                  <div class="col-md-3">
                    <label v-if="parentServiceId === 1">
                      {{ $t('message.my_reservations.duration') }}
                    </label>
                    <label v-else-if="globalState.services[parentServiceId] && globalState.services[parentServiceId].child_services_text">
                      {{ $t('message.booking.booking_step.service') }}
                    </label>
                    <label v-else-if="globalState.services[parentServiceId] && globalState.services[parentServiceId].has_service_items">
                      {{ $t('message.service_item.list_of_items') }}
                    </label>
                  </div>
                  <div class="col-md-9">
                    <div class="d-flex">
                      <div class="flex-fill">
                        <div v-if="parentServiceId === 1">
                          {{ order.work_hour }}
                          {{ $tc('message.hour', order.work_hour) }}
                        </div>
                        <div v-else-if="globalState.services[parentServiceId] && globalState.services[parentServiceId].child_services_text">
                          <span v-html="$options.filters.serviceParentName(order.service_id, globalState.services)"></span> • 
                          {{ globalState.services[order.service_id] | multiLangName }}
                          <!-- List of order purchases -->
                          <div class="pt-2" v-if="order.purchases.length > 0">
                            <span class="bullet-notification">
                              <i class="fas fa-circle"></i>
                            </span>
                            {{ $t('message.my_reservations.has_order_purchase') }} X{{ order.purchases.length }}
                          </div>
                        </div>
                        <div v-else-if="globalState.services[parentServiceId] && globalState.services[parentServiceId].has_service_items">
                          <div class="mb-1" v-for="orderServiceItem in order.order_service_items">
                            {{ orderServiceItem.service_item | multiLangName }}: {{ orderServiceItem.quantity }} {{ orderServiceItem.service_item.item_unit | multiLangName }}
                          </div>
                          <div class="text-info text-small mt-2">{{ $t('message.service_item.remark') }}</div>
                        </div>
                      </div>
                    </div>
                    <!-- Book additional hours -->
                    
                    <div class="pt-3" v-if="canBookAdditionalHours(order)">
                      <router-link class="btn btn-info btn-block" :to="`/reservation/${order.id}/additional-hours`">
                        {{ $t('message.my_reservations.additional_hours') }}
                      </router-link>
                    </div>
                    
                    
                    <!-- Book more ironing cloth -->
                    <div class="pt-3" v-if="order.professional_id && parentServiceId === 2">
                      <router-link class="btn btn-info btn-block" :to="`/reservation/${order.id}/purchase-service`">
                        {{ $t('message.my_reservations.book_additional_ironing_service') }}
                      </router-link>
                    </div>
                    
                    <!-- List of order additional hours -->
                    <order-additional-hours :order="order"/>

                  </div>
                </div>
              </div>

              <div class="card-body" v-if="order.extra">
                <div class="row no-gutters">
                  <div class="col-md-3 col-12">
                    <label>{{ $t('message.my_reservations.extras') }}</label>
                  </div>
                  <div class="col-md-9 col-12">
                    {{ order.extra | multiLangName }}
                    <div class="pt-2" v-if="order.purchases.length > 0">
                      <span class="bullet-notification">
                                <i class="fas fa-circle"></i>
                              </span>
                      {{ $t('message.my_reservations.has_order_purchase') }} X{{ order.purchases.length }}
                    </div>
                    <div class="pt-3" v-if="order.professional_id && order.extra.service_id === 2">
                      <router-link class="btn btn-info btn-block" :to="`/reservation/${order.id}/purchase-service`">
                        {{ $t('message.my_reservations.book_additional_ironing_service') }}
                      </router-link>
                    </div>
                  </div>
                </div>
              </div>

              <div class="card-body">
                <div class="row no-gutters">
                  <div class="col-md-3">
                    <label>{{ $t('message.my_reservations.customer_name') }}</label>
                  </div>
                  <div class="col-md-9">
                    {{ order.customer_name }}
                  </div>
                </div>
              </div>
              <div class="card-body">
                <div class="row no-gutters">
                  <div class="col-md-3">
                    <label>{{ $t('message.my_reservations.customer_address') }}</label>
                  </div>
                  <div class="col-md-9">
                    <p v-if="order.place_name">{{ order.place_name }}</p>
                    <p>
                      {{ $t(globalState.placeSizes[order.place_size_id]) | multiLangName }}
                    </p>
                    <p>{{ order.address }}</p>
                  </div>
                </div>
                <div class="row no-gutters" v-if="order.pet_remark || order.pet_types">
                  <div class="col-md-3">
                    <label>
                      {{ $t('message.my_reservations.pet') }}
                    </label>
                  </div>
                  <div class="col-md-9">
                    <div class="d-flex justify-content-start align-items-center flex-wrap" v-if="order.pet_types">
                      <div v-for="petType in order.pet_types">
                        <div class="d-flex justify-content-start align-items-center border border-chip-gray rounded-pill px-2 py-1 mr-2 mb-2">
                          <div class="pr-2" v-if="petType.image_url">
                            <img :src="petType.image_url" width="24">
                          </div>
                          <div>{{ petType | multiLangName }} <span v-if="petType.remark">:{{ petType.remark }}</span></div>
                        </div>
                      </div>
                    </div>
                    <span v-if="order.pet_remark && !order.pet_types">
                      {{ order.pet_remark }}
                    </span>
                  </div>
                </div>
              </div>
              <div class="card-body" v-if="order.remark">
                <div class="row no-gutters">
                  <div class="col-md-3">
                    <label>{{ $t('message.my_reservations.remark') }}</label>
                  </div>
                  <div class="col-md-9" style="white-space: break-spaces;">{{ order.remark }}</div>
                </div>
              </div>
              <div class="card-body" v-if="order.customer_area_photos">
                <div class="mb-2">
                  <label>
                    {{ $t('message.booking.checkout_step.customer_area_photos') }}
                  </label>
                </div>
                <photos-album
                  :photos="order.customer_area_photos"
                  :photoType="'customer_area_photos'"
                  :includeGallery="true"
                />
              </div>
              <div class="card-body" v-if="order.service_id === 1 && order.user_place && order.user_place.checklist">
                <div class="row no-gutters">
                  <div class="col-md-3">
                    <label>{{ $t('message.my_reservations.checklist') }}</label>
                  </div>
                  <div class="col-md-9">
                    <template v-for="checklist in order.user_place.checklist">
                      <div class="mb-2">
                        <i class="fa fa-check"></i> {{ checklist }}
                      </div>
                    </template>
                  </div>
                </div>
              </div>
              <div class="card-body">
                <div class="row no-gutters">
                  <div class="col-md-3">
                    <label>{{ $t('message.my_reservations.payment') }}</label>
                  </div>
                  <div class="col-md-9">
                    <order-summary-total :order="order" v-if="order"/>
                  </div>
                </div>
              </div>
              <div class="card-body" v-if="order.professional_tip && order.professional_tip.paid_status">
                <div class="row no-gutters">
                  <div class="col-md-3 col-3">
                    <label class="mb-0">{{ $t('message.professional_tip.amount') }}</label>
                  </div>
                  <div class="col-md-9 col-9 text-right">
                    {{ Number(order.professional_tip.amount) | currency }}
                  </div>
                </div>
              </div>
              <div class="card-body" v-if="order.purchases.length > 0">
                <div class="row no-gutters">
                  <div class="col-md-3">
                    <label>{{ $t('message.my_reservations.other_payments') }}</label>
                  </div>
                  <div class="col-md-9">
                    <order-purchase :order="order"/>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- /.col left -->
          <div class="col-lg-4 text-center">
            <my-cleaner :order="order" 
                        :imgSize="48" 
                        class="d-none d-lg-block"/>
          </div>
          <!-- /.col right -->
        </div>
        <!-- /.row -->
        <!-- Back navigation for line liff -->
        <div class="container text-center pt-3 pb-4" v-if="isLineLoggedIn">
          <router-link to="/line-liff/bookings">
            <i class="fas fa-angle-left"></i> {{ $t('message.booking.new_booking.back') }}
          </router-link>
        </div>
      </template>
      <template v-else>
        <div class="row" v-if="!user">
          <div class="col-lg-6 offset-lg-3 text-center">
            <div class="card my-3">
              <div class="card-body">
                <div class="mt-3 mb-4">
                  {{ $t('message.my_reservations.login_to_view_detail') }}
                </div>
                <login/>
              </div>
            </div>
          </div>
        </div>
        <div class="p-4" v-else-if="user && order && Number(user.id) !== Number(order.user_id)">
          <div class="row">
            <div class="col text-center">
              <h3>
                {{ $t('message.my_reservations.cannot_view_other_user_reservation') }}
              </h3>
            </div>
          </div>
        </div>
        <div v-else>
          <loader/>
        </div>
      </template>
      <!-- Revise new booking date -->
      <revise-date-time-modal ref="reviseDateTimeModal"/>
      <!-- Orders in subscription -->
      <subscription-orders-modal ref="subscriptionOrdersModal"/>
      <!-- Cancel order modal -->
      <cancel-order-modal
        ref="cancelOrderModal"
        @cancelledOrder="cancelledOrder"
        :orderId="orderId"
        :userId="user.id"
        v-if="user"
      />
      <!-- Ironing service -->
      <purchase-service-modal ref="purchaseServiceModal" :parentServiceId="2"/>
    </section>
  </section>
</template>

<script>
  import { mapActions, mapGetters, mapState } from 'vuex'
  import ConfirmOrderPaymentSection from './components/ConfirmOrderPaymentSection'
  import ConfirmRequestAdditionalHoursSection from './components/ConfirmRequestAdditionalHoursSection'
  import FindNewProvider from './components/FineNewProvider'
  import Loader from '../loader/Loader'
  import Login from '../login/LoginForm'
  import MyCleaner from './components/MyCleaner'
  import OrderSummaryTotal from './components/OrderSummaryTotal'
  import PhotosAlbum from './components/PhotosAlbum'
  import ReceivedOrderSection from './components/ReceivedOrderSection'
  import ReviewModal from './components/ReviewModal'
  import ReviewSection from './components/ReviewSection'
  import ReviseDateTimeModal from './components/ReviseDateTimeModal'
  import SubscriptionOrdersModal from './components/SubscriptionOrdersModal'
  import CancelOrderModal from './components/CancelOrderModal'
  import SelectServiceDateTimeSection from './components/SelectServiceDateTimeSection'
  import ProfessionalTipSection from './components/ProfessionalTipSection'
  import PreBookingProfessionalSelectionSection from './components/PreBookingProfessionalSelectionSection'
  import PurchaseServiceModal from './components/PurchaseServiceModal'
  import OrderPurchase from './components/OrderPurchase'
  import moment from 'moment-timezone'
  import { orderMixin } from '../../mixins/orderMixin'
  import OrderAdditionalHours from './components/OrderAdditionalHours'
  import orderHelpers from '../../helpers/orderHelpers'
  import userEventLogHelpers from '../../helpers/userEventLogHelpers'

  export default {
    mixins: [orderMixin],
    components: {
      CancelOrderModal,
      ConfirmOrderPaymentSection,
      ConfirmRequestAdditionalHoursSection,
      FindNewProvider,
      Loader,
      Login,
      MyCleaner,
      OrderAdditionalHours,
      OrderPurchase,
      OrderSummaryTotal,
      PhotosAlbum,
      PreBookingProfessionalSelectionSection,
      ProfessionalTipSection,
      PurchaseServiceModal,
      ReceivedOrderSection,
      ReviewModal,
      ReviewSection,
      ReviseDateTimeModal,
      SelectServiceDateTimeSection,
      SubscriptionOrdersModal
    },
    data () {
      return {
        orderId: Number(this.$route.params.id),
        parentServiceId: null,
        parentService: null,
        selectedDateTime: {
          date: '',
          time: ''
        }
      }
    },
    computed: {
      ...mapState('auth', ['isLineLoggedIn']),
      ...mapGetters({
        isAddingReview: 'review/isAdding',
        isCancelingReviseDateTime: 'orderDetail/getIsCancelingReviseDateTime',
        isCancelingAdditionalHours: 'orderDetail/getIsCancelingAdditionalHours',
        isConfirmingReviseDateTime: 'orderDetail/getIsConfirmReviseDateTime',
        isFindingNewCleaner: 'orderDetail/getIsFindingNewCleanerOrderDetail',
        globalState: 'globalVal/getState',
        order: 'orderDetail/getOrderDetail',
        payment: 'payment/getPayment',
        reviewState: 'review/getState',
        state: 'orderDetail/getState',
        user: 'auth/getUser'
      })
    },
    watch: {
      order () {
        if (this.order) {
          this.getParentServiceId()
        }
      },
      async isFindingNewCleaner () {
        if (!this.state.isFindingNewCleaner) {
          await this.loadOrderDetail(this.order.id)
        }
      },
      isConfirmingReviseDateTime () {
        if (!this.state.isConfirmingReviseDateTime) {
          // Revise date time data to be null
          this.$store.commit('orderDetail/SET_ORDER_REVISE_DATE_TIME', null)
        }
      },
      isAddingReview () {
        if (!this.reviewState.isAdding) {
          const data = {key: 'is_reviewed', value: true}
          this.$store.commit('orderDetail/UPDATE_ORDER_DETAIL_DATA', data)
        }
      },
      'user': function (newVal, oldVal) {
        if (oldVal === null && newVal) {
          this.initLoad()
        }
      }
    },
    methods: {
      ...mapActions('orderDetail', ['loadOrderDetail']),
      repeatBooking (order) {
        // Generate repeat booking data
        orderHelpers.repeatBooking(order)
        // Save repeat booking log
        userEventLogHelpers.logEvent('repeat_booking', {
          order_id: order.id
        })
        this.$router.push('/booking/start?step=2')
      },
      async cancelledOrder (result) {
        if (result) {
          await this.loadOrderDetail(this.order.id)
          // this.$router.push('/my-reservations')
        }
      },
      isAbleReviseDateTime (order) {
        let result = false

        if (order.subscription === null) {
          const currentTime = moment(new Date())
          const cleaningTime = moment(new Date(order.cleaning_date))
          const duration = moment.duration(cleaningTime.diff(currentTime))
          const diffHours = duration.asHours()

          if (
            diffHours >= 24 &&
            order.revise_datetime_data === null &&
            order.paid_status > 0 &&
            order.status < 2 &&
            order.start_cleaning_date === null &&
            order.declined_order_data === null
          ) {
            result = true
          }
        }

        return result
      },
      canBookAdditionalHours (order) {
        let canBook = true
        if ((this.parentService && !this.parentService.can_book_more_hours) || !order.status || order.status === 2) {
          canBook = false
        } else {
          if (order && order.status === 1 && !order.cancel) {
            const bookMoreHours = order.book_more_hours
            if (bookMoreHours && bookMoreHours.length > 0) {
              if (!bookMoreHours[0].status) {
                canBook = false
              }
            }
          }
        }
        return canBook
      },
      displayNotification (order, type) {
        let result = false
        switch (type) {
          case 'decliningBooking':
            if (
              order.declined_order_data &&
              order.selected_professional_id > 0 &&
              order.status < 2
            ) {
              result = true
            }
            break
          case 'revisingDateTime':
            if (
              order.revise_datetime_data &&
              order.revise_datetime_data.sender_type === 'professional'
            ) {
              result = true
            }
            break
        }
        return result
      },
      openCancelOrderModal () {
        this.$refs.cancelOrderModal.openModal()
      },
      confirmFindNewCleaner () {
        this.$swal({
          title: this.$i18n.t('message.declined_booking.find_new_cleaner_tile'),
          text: this.$i18n.t('message.declined_booking.find_new_cleaner_detail'),
          type: 'info',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#9E9E9E',
          reverseButtons: true,
          confirmButtonText: this.$i18n.t('message.confirm.yes'),
          cancelButtonText: this.$i18n.t('message.confirm.no')
        }).then(() => {
          // Find new cleaner
          const submitData = {
            order_id: this.order.id,
            user_id: this.user.id,
            validation: ''
          }
          this.$store.dispatch('orderDetail/FIND_NEW_CLEANER', submitData)
        })
      },
      cancelReviseDateTime () {
        this.$swal({
          title: this.$i18n.t('message.confirm.title'),
          text: this.$i18n.t('message.confirm.cancel_revise_datetime'),
          type: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#9E9E9E',
          reverseButtons: true,
          confirmButtonText: this.$i18n.t('message.confirm.yes'),
          cancelButtonText: this.$i18n.t('message.confirm.no')
        }).then(() => {
          const submitData = {
            order_id: this.orderId,
            user_id: this.user.id
          }
          this.$store.dispatch('orderDetail/CANCEL_REVISE_DATE_TIME', submitData)
        })
      },

      unsubscribe () {
        this.$swal({
          title: this.$i18n.t('message.subscription.unsubscribe_title'),
          text: this.$i18n.t('message.subscription.unsubscribe_detail'),
          type: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#9E9E9E',
          reverseButtons: true,
          confirmButtonText: this.$i18n.t('message.confirm.yes'),
          cancelButtonText: this.$i18n.t('message.confirm.no')
        }).then(() => {
          this.$store.dispatch('subscription/UNSUBSCRIBE', this.order)
        })
      },
      openReviseDateTimeModal () {
        const arrDateTime = this.order.cleaning_date.split(' ')
        const arrDate = arrDateTime[0].split('-')
        const date = arrDate[2] + '/' + arrDate[1] + '/' + arrDate[0]
        const time = arrDateTime[1].slice(0, -3)
        this.$refs.reviseDateTimeModal.setSelectedDateTime(
          this.orderId,
          date,
          time
        )
        this.$refs.reviseDateTimeModal.showReviseDateTimeModal(true)
      },
      openSubscriptionOrdersModal () {
        this.$refs.subscriptionOrdersModal.showSubscriptionOrdersModal(true)
      },
      openPurchaseServiceModal () {
        this.$refs.purchaseServiceModal.showModal(true)
      },
      acceptReviseDateTime () {
        this.$swal({
          title: this.$i18n.t('message.confirm.title'),
          text: this.$i18n.t('message.confirm.accept_revise_datetime'),
          type: 'info',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#9E9E9E',
          reverseButtons: true,
          confirmButtonText: this.$i18n.t('message.confirm.yes'),
          cancelButtonText: this.$i18n.t('message.confirm.no')
        }).then(() => {
          this.confirmOrderReviseDateTime('accept')
        })
      },
      declineReviseDateTime () {
        this.$swal({
          title: this.$i18n.t('message.confirm.title'),
          text: this.$i18n.t('message.confirm.decline_revise_datetime'),
          type: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#9E9E9E',
          reverseButtons: true,
          confirmButtonText: this.$i18n.t('message.confirm.yes'),
          cancelButtonText: this.$i18n.t('message.confirm.no')
        }).then(() => {
          this.confirmOrderReviseDateTime('decline')
        })
      },
      confirmOrderReviseDateTime (confirmAction) {
        let submitData = null
        if (this.user) {
          submitData = {
            order_id: this.orderId,
            user_id: this.user.id,
            validation: '',
            confirm_action: confirmAction
          }
        } else {
          submitData = {
            order_id: this.orderId,
            user_id: 0,
            validation: this.validation,
            confirm_action: confirmAction
          }
        }
        this.$store.dispatch('orderDetail/CONFIRM_REVISE_DATE_TIME', submitData)
      },
      getParentServiceId () {
        this.parentServiceId = this.order.service_id
        if (this.globalState.services[this.order.service_id].parent_id > 0) {
          this.parentServiceId = this.globalState.services[this.order.service_id].parent_id
        }
        this.parentService = this.globalState.services[this.parentServiceId]
      },
      initLoad () {
        this.orderId = Number(this.$route.params.id)
        this.$store.commit('orderDetail/SET_ORDER_DETAIL', null)
        this.loadOrderDetail(this.orderId)
      }
    },
    mounted () {
      this.initLoad()
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/order/OrderDetail.vue